import { DataGrid, type GridColumn } from "@/components/data-grid";
import { EmptyState } from "@/components/EmptyState";
import { UsageIcon, WarningIcon } from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { cn } from "cn";
import { lazy, Suspense, useState, type ReactNode } from "react";

import { formatDate } from "../lib/dates.ts";
import { PERMISSIONS } from "../lib/permissions.ts";
import { errorMessage } from "../lib/query.ts";

import { useDashboardPeriod } from "../app/DashboardShell.tsx";
import { useSession } from "../app/session.tsx";
import { Figure } from "../chat/components/Figure.tsx";
import { UNTITLED_CONVERSATION } from "../chat/conversations.ts";
import { dailyMeasure } from "../usage/daily-measure.ts";
import { USAGE_TEXTS as TEXTS } from "../usage/texts.ts";
import {
  describeCost,
  formatTokens,
  formatUsd,
  modelPricesQuery,
  taskLabel,
  totalTokens,
  UNKNOWN_PRICE,
  usageSummaryQuery,
  useSyncModelPrices,
  type ModelPrice,
  type UsageSummary,
  type UsageTotals,
} from "../usage/usage.ts";
import { isWholeYear, periodBounds, periodLabel } from "./periods.ts";

/** ECharts solo se descarga si alguien abre esta pantalla. */
const DailyUsageChart = lazy(async () => {
  const module = await import("../usage/DailyUsageChart.tsx");
  return { default: module.DailyUsageChart };
});

/* ------------------------------------------------------------------------ */
/* Columnas: fuera del componente, o la tabla se reconstruye en cada render. */
/* ------------------------------------------------------------------------ */

/** La celda del coste: lo desconocido se dice, y lo parcial también. */
function CostCell({ totals }: { totals: UsageTotals }) {
  const cost = describeCost(totals.costUsd, totals.unpricedCalls);
  return (
    <span
      className={cn(cost.kind !== "estimated" && "text-warning")}
      title={cost.long}
    >
      {cost.amount}
    </span>
  );
}

/** Las columnas que comparten todos los desgloses. */
const totalsColumns = <T extends UsageTotals>(): GridColumn<T>[] => [
  {
    key: "input",
    label: TEXTS.panel.columns.input,
    numeric: true,
    render: (row) => row.input.toLocaleString("es"),
    sortValue: (row) => row.input,
  },
  {
    key: "cache",
    label: TEXTS.panel.columns.cache,
    numeric: true,
    render: (row) =>
      (row.cachedInput + row.cacheWriteInput).toLocaleString("es"),
    sortValue: (row) => row.cachedInput + row.cacheWriteInput,
  },
  {
    key: "output",
    label: TEXTS.panel.columns.output,
    numeric: true,
    render: (row) => row.output.toLocaleString("es"),
    sortValue: (row) => row.output,
  },
  {
    key: "cost",
    label: TEXTS.panel.columns.cost,
    numeric: true,
    render: (row) => <CostCell totals={row} />,
    // Lo desconocido ordena al final, no como si fuera cero.
    sortValue: (row) => row.costUsd ?? -1,
  },
];

type ConversationRow = UsageSummary["byConversation"][number];
type TaskRow = UsageSummary["byTask"][number];
type ModelRow = UsageSummary["byModel"][number];
type DayRow = UsageSummary["byDay"][number];

/** Cómo se llama en el consumo una conversación que ya no existe. */
const DELETED_CONVERSATION = TEXTS.panel.deletedConversation;

const CONVERSATION_COLUMNS: readonly GridColumn<ConversationRow>[] = [
  {
    key: "title",
    label: TEXTS.panel.columns.conversation,
    render: (row) =>
      /* Borrada: su consumo sigue contando --ese dinero se gastó-- pero ya
         no hay conversación que abrir (R4.4). Sin el aviso se leía como una
         viva a la que le faltaba el título, y el enlace llevaba a nada. */
      row.deleted ? (
        <span className="text-muted-foreground italic">
          {DELETED_CONVERSATION}
        </span>
      ) : (
        // Lleva a la conversación, en Chat: es donde se ve qué la hizo cara.
        <Link
          to="/"
          search={(previous) => ({ ...previous, c: row.conversationId })}
          className="hover:text-primary hover:underline"
        >
          {row.title ?? UNTITLED_CONVERSATION}
        </Link>
      ),
    sortValue: (row) =>
      row.deleted ? DELETED_CONVERSATION : (row.title ?? UNTITLED_CONVERSATION),
  },
  ...totalsColumns<ConversationRow>(),
];

const TASK_COLUMNS: readonly GridColumn<TaskRow>[] = [
  {
    key: "task",
    label: TEXTS.panel.columns.task,
    render: (row) => taskLabel(row.task),
    sortValue: (row) => taskLabel(row.task),
  },
  ...totalsColumns<TaskRow>(),
];

const MODEL_COLUMNS: readonly GridColumn<ModelRow>[] = [
  {
    key: "model",
    label: TEXTS.panel.columns.model,
    render: (row) => <code className="text-xs">{row.model}</code>,
    sortValue: (row) => row.model,
  },
  ...totalsColumns<ModelRow>(),
];

const DAY_COLUMNS: readonly GridColumn<DayRow>[] = [
  {
    key: "day",
    label: TEXTS.panel.columns.day,
    render: (row) => formatDate(row.day),
    sortValue: (row) => row.day,
  },
  ...totalsColumns<DayRow>(),
];

const perMtok = (value: number | null): string =>
  value === null ? "—" : formatUsd(value);

const PRICE_COLUMNS: readonly GridColumn<ModelPrice>[] = [
  {
    key: "model",
    label: TEXTS.panel.columns.model,
    render: (row) => <code className="text-xs">{row.model}</code>,
    sortValue: (row) => row.model,
  },
  {
    key: "input",
    label: TEXTS.panel.columns.input,
    numeric: true,
    render: (row) => perMtok(row.inputPerMtok),
    sortValue: (row) => row.inputPerMtok,
  },
  {
    key: "cacheRead",
    label: TEXTS.panel.columns.cacheRead,
    numeric: true,
    render: (row) => perMtok(row.cacheReadPerMtok),
    sortValue: (row) => row.cacheReadPerMtok,
  },
  {
    key: "cacheWrite",
    label: TEXTS.panel.columns.cacheWrite,
    numeric: true,
    render: (row) => perMtok(row.cacheWritePerMtok),
    sortValue: (row) => row.cacheWritePerMtok,
  },
  {
    key: "output",
    label: TEXTS.panel.columns.output,
    numeric: true,
    render: (row) => perMtok(row.outputPerMtok),
    sortValue: (row) => row.outputPerMtok,
  },
  {
    key: "source",
    label: TEXTS.panel.columns.source,
    // Un precio puesto a mano gana al sincronizado: tiene que verse cuál es.
    render: (row) =>
      row.source === "manual" ? TEXTS.panel.manual : TEXTS.panel.synced,
    sortValue: (row) => row.source,
  },
  {
    key: "validFrom",
    label: TEXTS.panel.columns.validFrom,
    render: (row) => formatDate(row.validFrom),
    sortValue: (row) => row.validFrom,
  },
];

/* ------------------------------------------------------------------------ */
/* La pantalla                                                               */
/* ------------------------------------------------------------------------ */

/**
 * El consumo de tokens y su coste estimado (2.4.4).
 *
 * Mira el MISMO periodo que el resto del panel --el que elige la cabecera--,
 * así que "septiembre" significa lo mismo aquí que en el Resumen. Lo que
 * cuenta es todo lo que se le pidió a un modelo, incluido lo que corre en el
 * worker y nadie ve: extraer memoria, compactar, poner títulos.
 */
export function UsagePanel() {
  const { period } = useDashboardPeriod();
  const range = periodBounds(period);
  const summary = useQuery(usageSummaryQuery(range));
  const data = summary.data;

  return (
    // `*:shrink-0`: esta pantalla scrollea entera. Sin eso, con poca altura
    // los marcos --que recortan lo que se sale-- se encogen hasta una raya.
    <section className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 *:shrink-0">
      <header>
        <p className="text-sm text-muted-foreground">
          {isWholeYear(period)
            ? TEXTS.panel.wholeYear(period)
            : periodLabel(period)}
          {TEXTS.panel.introAfterPeriod}
          <strong>{TEXTS.panel.introStrong}</strong>
          {TEXTS.panel.introEnd}
        </p>
      </header>

      {summary.error != null && (
        <Alert variant="destructive">
          <AlertTitle>{TEXTS.panel.readError}</AlertTitle>
          <AlertDescription>{errorMessage(summary.error)}</AlertDescription>
        </Alert>
      )}

      {summary.isPending && (
        <p className="text-sm text-muted-foreground">{TEXTS.panel.loading}</p>
      )}

      {data !== undefined && <Summary data={data} />}

      <Prices />
    </section>
  );
}

function Summary({ data }: { data: UsageSummary }) {
  const totals = data.totals;
  const cost = describeCost(totals.costUsd, totals.unpricedCalls);
  const [dailyAsTable, setDailyAsTable] = useState(false);
  /**
   * El backend manda las conversaciones ya ordenadas, más caras primero, y
   * dice cuántas destacar. Se enseñan esas y el resto a un clic: la lista
   * entera de un mes es para buscar, no para leer.
   */
  const [allConversations, setAllConversations] = useState(false);
  const conversations = allConversations
    ? data.byConversation
    : data.byConversation.slice(0, data.topConversations);
  const hiddenConversations = data.byConversation.length - conversations.length;
  // Solo cuando se pinta el coste: si se pintan tokens, todos tienen barra.
  const unpricedDays =
    dailyMeasure(data.byDay) === "cost"
      ? data.byDay.filter((day) => day.costUsd === null && totalTokens(day) > 0)
          .length
      : 0;

  if (totalTokens(totals) === 0) {
    return <EmptyState icon={UsageIcon}>{TEXTS.panel.empty}</EmptyState>;
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label={TEXTS.panel.stats.cost}
          warn={cost.kind !== "estimated"}
          {...(data.backfilled > 0
            ? {
                note: TEXTS.panel.stats.backfilled(
                  data.backfilled.toLocaleString("es"),
                  data.backfilled === 1,
                ),
              }
            : {})}
        >
          {cost.amount}
        </Stat>
        <Stat label={TEXTS.panel.stats.tokens}>
          {formatTokens(totalTokens(totals))}
        </Stat>
        <Stat label={TEXTS.panel.stats.calls}>
          {totals.calls.toLocaleString("es")}
        </Stat>
        <Stat
          label={TEXTS.panel.stats.unpriced}
          warn={totals.unpricedCalls > 0}
        >
          {totals.unpricedCalls === 0
            ? TEXTS.panel.stats.none
            : TEXTS.panel.stats.unpricedCalls(
                totals.unpricedCalls.toLocaleString("es"),
              )}
        </Stat>
      </div>

      <Figure
        title={
          dailyMeasure(data.byDay) === "cost"
            ? TEXTS.panel.daily.cost
            : TEXTS.panel.daily.tokens
        }
        aside={
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => {
              setDailyAsTable(!dailyAsTable);
            }}
          >
            {dailyAsTable
              ? TEXTS.panel.daily.viewChart
              : TEXTS.panel.daily.viewTable}
          </button>
        }
        {...(!dailyAsTable && unpricedDays > 0
          ? {
              footer: (
                <p className="px-4 py-2 text-xs text-warning">
                  {TEXTS.panel.daily.unpricedDays(unpricedDays)}
                  {TEXTS.panel.daily.unpricedDaysNote}
                </p>
              ),
            }
          : {})}
      >
        {dailyAsTable ? (
          <DataGrid columns={DAY_COLUMNS} rows={data.byDay} />
        ) : (
          <Suspense
            fallback={
              <p className="px-4 py-3 text-sm text-muted-foreground">
                {TEXTS.panel.daily.loadingChart}
              </p>
            }
          >
            <DailyUsageChart days={data.byDay} />
          </Suspense>
        )}
      </Figure>

      <Figure
        title={TEXTS.panel.byConversation}
        aside={
          <span className="text-xs text-muted-foreground">
            {TEXTS.panel.mostExpensiveFirst}
          </span>
        }
        {...(hiddenConversations > 0
          ? {
              footer: (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setAllConversations(true);
                  }}
                >
                  {TEXTS.panel.viewAll(
                    hiddenConversations.toLocaleString("es"),
                  )}
                </button>
              ),
            }
          : {})}
      >
        <DataGrid columns={CONVERSATION_COLUMNS} rows={conversations} />
      </Figure>

      {/* Lado a lado solo cuando caben enteras: con nombres de modelo
          largos, a media anchura la columna del coste quedaba escondida
          detrás de un scroll. `*:min-w-0` para que, si aun así no cabe, se
          desplace dentro de su marco en vez de salirse. */}
      <div className="grid gap-4 *:min-w-0 2xl:grid-cols-2">
        <Figure title={TEXTS.panel.byTask}>
          <DataGrid columns={TASK_COLUMNS} rows={data.byTask} />
        </Figure>
        <Figure title={TEXTS.panel.byModel}>
          <DataGrid columns={MODEL_COLUMNS} rows={data.byModel} />
        </Figure>
      </div>
    </>
  );
}

function Stat({
  label,
  warn = false,
  note,
  children,
}: {
  label: string;
  warn?: boolean;
  /**
   * Una nota discreta bajo la cifra. La usa el total cuando el mes incluye
   * turnos rellenados de antes del libro (R4.2): se sabe cuánto leyeron pero
   * no cuánto escribieron en caché, así que el total se queda corto.
   */
  note?: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-1 flex items-center gap-1.5 text-xl font-semibold tabular-nums",
          warn && "text-warning",
        )}
      >
        {warn && <WarningIcon className="size-4 shrink-0" aria-hidden />}
        {children}
      </p>
      {note !== undefined && (
        <p className="mt-1 text-xs text-muted-foreground">{note}</p>
      )}
    </div>
  );
}

/**
 * El catálogo de precios: de dónde sale cada estimación.
 *
 * Los precios no están en el código ni se sacan de memoria: se sincronizan
 * desde una fuente pública. Aquí se ve cuándo fue la última vez, si falló, y
 * qué modelos no casan con ningún precio --sus llamadas cuentan tokens pero
 * no coste, y eso tiene que estar dicho--.
 */
function Prices() {
  const [all, setAll] = useState(false);
  const prices = useQuery(modelPricesQuery(all));
  const sync = useSyncModelPrices();
  /* Sincronizar reescribe los precios con los que se estima todo el coste:
     es `usage.write`, no basta con leer el consumo. */
  const canSync = useSession().can(PERMISSIONS.usageWrite);
  const lastSync = prices.data?.lastSync ?? null;
  const lastGood = prices.data?.lastSuccessfulSync ?? null;
  const routing = prices.data?.routing ?? [];

  return (
    <Figure
      title={TEXTS.panel.prices.title}
      aside={
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {lastSync === null
              ? TEXTS.panel.prices.neverSynced
              : TEXTS.panel.prices.lastSync(formatDate(lastSync.startedAt))}
            {lastSync?.status === "error" && (
              <span className="text-warning">{TEXTS.panel.prices.failed}</span>
            )}
          </span>
          {canSync && (
            <Button
              size="sm"
              variant="outline"
              disabled={sync.isPending}
              onClick={() => {
                sync.mutate();
              }}
            >
              {sync.isPending
                ? TEXTS.panel.prices.syncing
                : TEXTS.panel.prices.sync}
            </Button>
          )}
        </div>
      }
      footer={
        <button
          type="button"
          className="w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
          onClick={() => {
            setAll(!all);
          }}
        >
          {all ? TEXTS.panel.prices.onlyUsed : TEXTS.panel.prices.allModels}
        </button>
      }
    >
      <div className="grid gap-3 p-3 empty:hidden">
        {sync.error != null && (
          <Alert variant="destructive">
            <AlertTitle>{TEXTS.panel.prices.syncError}</AlertTitle>
            <AlertDescription>{errorMessage(sync.error)}</AlertDescription>
          </Alert>
        )}
        {/* Lo que acaba de pasar al pulsar, si fue bien. Si la fuente falló
            no hay 5xx: lo cuenta `lastSync`, que ya se ha vuelto a leer. */}
        {sync.data?.status === "ok" && (
          <p className="text-sm text-muted-foreground">
            {TEXTS.panel.prices.synced(
              sync.data.inserted.toLocaleString("es"),
              sync.data.checked.toLocaleString("es"),
            )}
          </p>
        )}
        {lastSync?.status === "error" && (
          <Alert className="border-warning">
            <AlertTitle>{TEXTS.panel.prices.lastFailed}</AlertTitle>
            <AlertDescription>
              <p>
                {TEXTS.panel.prices.keepUsing}
                {lastGood === null
                  ? TEXTS.panel.prices.noGoodSync
                  : TEXTS.panel.prices.fromSync(formatDate(lastGood.startedAt))}
                . {lastSync.error}
              </p>
            </AlertDescription>
          </Alert>
        )}
        {prices.data !== undefined && prices.data.unpriced.length > 0 && (
          <Alert className="border-warning">
            <AlertTitle>{TEXTS.panel.prices.unpricedModels}</AlertTitle>
            <AlertDescription>
              <p>
                <code>{prices.data.unpriced.join(", ")}</code>
                {TEXTS.panel.prices.unpricedNote}
              </p>
            </AlertDescription>
          </Alert>
        )}
        {prices.error != null && (
          <Alert variant="destructive">
            <AlertTitle>{TEXTS.panel.prices.readError}</AlertTitle>
            <AlertDescription>{errorMessage(prices.error)}</AlertDescription>
          </Alert>
        )}
        {routing.length > 0 && (
          // Qué modelo hace cada tarea: es lo que explica el desglose de arriba.
          <ul className="flex flex-wrap gap-2 text-xs">
            {routing.map((route) => (
              <li
                key={route.task}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-2.5 py-1",
                  !route.priced && "border-warning text-warning",
                )}
              >
                {!route.priced && (
                  <WarningIcon className="size-3.5" aria-hidden />
                )}
                {taskLabel(route.task)}
                <span className="text-muted-foreground">→</span>
                <code>{route.model}</code>
                {!route.priced && <span>· {UNKNOWN_PRICE}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <DataGrid
        columns={PRICE_COLUMNS}
        rows={prices.data?.prices ?? []}
        empty={
          prices.isPending ? TEXTS.panel.loading : TEXTS.panel.prices.empty
        }
      />
    </Figure>
  );
}
